/**
 * A name built from what is already known about an item (FR-2.19).
 *
 *   <brand> <model> <category> - <colors>
 *   Patagonia Houdini Jacket - Blue
 *
 * Capture is the thing this app asks people to do most, and the name is the one
 * field that cannot be left blank -- so it is the field most likely to be the
 * reason a thing never gets catalogued at all. Everything in the template is
 * already being typed into the form beside it.
 *
 * A suggestion, never an imposition: it fills the box and the box stays a box.
 * There is no rule here about what an item must be called, which is why this is
 * a button and not something that happens on save.
 */

/** The category's own name, not its path: "Jacket", not "Clothing / Jacket". */
export interface NameParts {
  readonly brand?: string | undefined;
  readonly model?: string | undefined;
  readonly category?: string | undefined;
  readonly colors?: readonly string[] | undefined;
}

/**
 * Joins what is there and omits what is not.
 *
 * The separators belong to the pieces around them rather than to the template,
 * which is what keeps an absent field from leaving its punctuation behind --
 * "Patagonia Jacket" when there is no model, not "Patagonia  Jacket", and
 * "Patagonia Jacket" when there is no colour, not "Patagonia Jacket - ".
 *
 * A colour with nothing before it is the whole name rather than "- Blue": a
 * dash separates two things, and there is only one.
 */
export function composeItemName(parts: NameParts): string {
  const clean = (value: string | undefined): string => (value ?? '').trim();

  const described = [clean(parts.brand), clean(parts.model), clean(parts.category)]
    .filter((part) => part !== '')
    .join(' ');

  const colored = (parts.colors ?? [])
    .map((color) => color.trim())
    .filter((color) => color !== '')
    // The separator the colour field itself stores, so a name reads the way the
    // field it came from does.
    .join('/');

  if (described === '') return colored;
  if (colored === '') return described;
  return `${described} - ${colored}`;
}
